import React, { Component } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Async } from 'react-select';
import Swal from 'sweetalert2';
import gql from 'graphql-tag';
import Loader from '../../components/CustomLoader';
import client from '../../utils/apolloClient';
import { getUser } from '../../data-services/queries';
import getData, { imageUpload, pdfUpload } from '../../data-services/api';
import { MAX_UPLOAD_BYTES } from '../../config/uploadLimits';
import { getPDFBase64 } from '../../components/ImageGallery';
import pdfLogo from '../../components/Images/pdfLogo.png';
import {
    MAX_SUBMISSIONS,
    STATUS,
    ammdHasAccessToFcp,
    cmdiHasAccessToFcp,
    getById,
    listByBatch,
    listByFcp,
    listByEvaluator,
    makeBatchKey,
    getEntryKey,
    probeTable,
    saveOwnerBatch,
    searchFcpEvaluators,
    submitEvaluation,
    toSqlValue,
} from './ettcSubmissionApi';
import './ettcSubmission.css';

const queryString = require('query-string');
const MAX_UPLOAD_MB = MAX_UPLOAD_BYTES / (1024 * 1024);
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

let localKey = 0;
const nextKey = () => {
    localKey += 1;
    return `new-${Date.now()}-${localKey}`;
};

const emptySection = (submissionNumber) => ({
    key: nextKey(),
    id: null,
    submissionNumber,
    imageUrl: null,
    imagePreview: null,
    pdfUrl: null,
    score: '',
    status: STATUS.PENDING,
    dirty: true,
});

const remarkFromRows = (rows) => {
    const withRemark = (rows || []).find(
        (row) => row && row.evaluation_comments__c && String(row.evaluation_comments__c).trim()
    );
    return withRemark ? String(withRemark.evaluation_comments__c) : '';
};

const EXTRA_REMARK_FIELDS = [
    {
        key: 'overallClayModellingTechniqueOutcome',
        column: 'overall_clay_modelling_technique_outcome__c',
        label: 'Overall Clay Modelling Technique Outcome',
    },
    {
        key: 'overallFeedbackAdditionalRemarks',
        column: 'overall_feedback_additional_remarks__c',
        label: 'Overall Feedback Additional Remarks',
    },
    {
        key: 'rightUsageOfProductsMaterials',
        column: 'right_usage_of_products_materials__c',
        label: 'Right Usage of Products Materials',
    },
    {
        key: 'submissionPresentation',
        column: 'submission_presentation__c',
        label: 'Submission Presentation',
    },
    {
        key: 'overallPaintingTechniqueOutcome',
        column: 'overall_painting_technique_outcome__c',
        label: 'Overall Painting Technique Outcome',
    },
];

const emptyExtraRemarks = () =>
    EXTRA_REMARK_FIELDS.reduce((acc, field) => {
        acc[field.key] = '';
        return acc;
    }, {});

const fieldFromRows = (rows, column) => {
    const withValue = (rows || []).find(
        (row) => row && row[column] && String(row[column]).trim()
    );
    return withValue ? String(withValue[column]).trim() : '';
};

const extraRemarksFromRows = (rows) =>
    EXTRA_REMARK_FIELDS.reduce((acc, field) => {
        acc[field.key] = fieldFromRows(rows, field.column);
        return acc;
    }, {});

const submissionNameFromRows = (rows) => {
    const withName = (rows || []).find(
        (row) => row && row.submission_name__c && String(row.submission_name__c).trim()
    );
    return withName ? String(withName.submission_name__c).trim() : '';
};

const mapRowToSection = (row) => ({
    key: `id-${row.id}`,
    id: row.id,
    submissionNumber: row.submission_number__c,
    imageUrl: row.submission_image__c || null,
    imagePreview: row.submission_image__c || null,
    pdfUrl: row.submission_document__c || null,
    score: row.score__c != null && row.score__c !== '' ? String(row.score__c) : '',
    status: row.submission_status__c || STATUS.PENDING,
    evaluatedBySfid: row.evaluated_by__c,
    evaluatedByName: row.evaluated_by_name,
    fcpSfid: row.fcp__c,
    fcpName: row.fcp_name,
    dirty: false,
});

class ETTCSubmissionForm extends Component {
    constructor(props) {
        super(props);
        const params = queryString.parse(
            (props.location && props.location.search) || window.location.search || ''
        );
        this.state = {
            loading: true,
            saving: false,
            uploading: false,
            userData: null,
            mode: 'owner',
            focusId: params.id || null,
            forcedMode: params.mode || null,
            backPath: params.back || null,
            fcpSfid: null,
            fcpDisplayName: '',
            evaluatedBy: null,
            submissionDate: new Date().toISOString().slice(0, 10),
            submissionName: '',
            remark: '',
            ...emptyExtraRemarks(),
            sections: [],
            batchKey: null,
            editing: false,
        };
        this.fileInputs = {};
    }

    loadFcpDisplayName = async (sfid) => {
        if (!sfid) return '';
        try {
            const res = await getData(
                `SELECT name FROM salesforce.Account WHERE sfid = '${toSqlValue(sfid)}' LIMIT 1`
            );
            const row = res && res.result && res.result[0];
            return (row && row.name) || '';
        } catch (e) {
            return '';
        }
    };

    buildOwnerState = async (userData, existingRows, batchKey) => {
        const fcpDisplayName = await this.loadFcpDisplayName(userData.sfid);
        const sections = existingRows.length
            ? existingRows.map(mapRowToSection)
            : [emptySection(1)];

        let evaluatedBy = null;
        let submissionDate = new Date().toISOString().slice(0, 10);
        let submissionName = '';
        const firstRow = existingRows[0];
        if (firstRow) {
            if (firstRow.evaluated_by__c) {
                evaluatedBy = {
                    label: firstRow.evaluated_by_name || firstRow.evaluated_by__c,
                    value: firstRow.evaluated_by__c,
                };
            }
            if (firstRow.submission_date__c) {
                submissionDate = String(firstRow.submission_date__c).slice(0, 10);
            }
            submissionName = submissionNameFromRows(existingRows);
        }

        return {
            mode: 'owner',
            fcpSfid: userData.sfid,
            fcpDisplayName,
            evaluatedBy,
            submissionDate,
            submissionName,
            sections,
            remark: remarkFromRows(existingRows),
            ...extraRemarksFromRows(existingRows),
            batchKey: batchKey || makeBatchKey(),
            editing: existingRows.length === 0,
            loading: false,
        };
    };

    async componentDidMount() {
        const { data } = await client.query({ query: gql(getUser) });
        const userData = data && data.user;
        this.setState({ userData });

        const { focusId, forcedMode } = this.state;
        const params = queryString.parse(
            (this.props.location && this.props.location.search) ||
                window.location.search ||
                ''
        );
        if (!userData) {
            this.setState({ loading: false, mode: 'deny' });
            return;
        }

        const probe = await probeTable();
        if (!probe.ok) {
            this.setState({ loading: false, mode: 'deny' });
            Swal.fire({
                icon: 'warning',
                title: 'ETTC table not ready',
                text:
                    'FCP_Submission__c is not available via Heroku Connect yet. Ask admin to sync the object, then retry.',
            });
            return;
        }

        try {
            // FCP owner / evaluator
            if (userData.role === 'FCP' && !forcedMode) {
                if (focusId) {
                    const focused = await getById(focusId);
                    if (focused && focused.evaluated_by__c === userData.sfid) {
                        const batchKey = getEntryKey(focused);
                        const assigned = (await listByEvaluator(userData.sfid))
                            .filter(
                                (r) =>
                                    r.fcp__c === focused.fcp__c &&
                                    getEntryKey(r) === batchKey
                            )
                            .sort(
                                (a, b) =>
                                    (parseInt(a.submission_number__c, 10) || 0) -
                                    (parseInt(b.submission_number__c, 10) || 0)
                            );
                        const sections = (assigned.length ? assigned : [focused]).map(
                            mapRowToSection
                        );
                        const evalName = await this.loadFcpDisplayName(userData.sfid);
                        const submissionDate = focused.submission_date__c
                            ? String(focused.submission_date__c).slice(0, 10)
                            : '';
                        const groupRows = assigned.length ? assigned : [focused];
                        this.setState({
                            mode: 'evaluator',
                            fcpSfid: focused.fcp__c,
                            fcpDisplayName: focused.fcp_name || '',
                            evaluatedBy: {
                                label: evalName || 'You',
                                value: userData.sfid,
                            },
                            submissionDate,
                            submissionName: submissionNameFromRows(groupRows),
                            sections,
                            remark: remarkFromRows(groupRows),
                            ...extraRemarksFromRows(groupRows),
                            batchKey,
                            loading: false,
                        });
                        return;
                    }
                    if (focused && focused.fcp__c === userData.sfid) {
                        const batchKey = getEntryKey(focused);
                        const existingRows = await listByBatch(userData.sfid, batchKey);
                        this.setState(
                            await this.buildOwnerState(
                                userData,
                                existingRows.length ? existingRows : [focused],
                                batchKey
                            )
                        );
                        return;
                    }
                    if (focused && focused.fcp__c !== userData.sfid) {
                        this.setState({ loading: false, mode: 'deny' });
                        Swal.fire({
                            icon: 'error',
                            title: 'Access denied',
                            text: 'You do not have access to this submission.',
                        });
                        return;
                    }
                }

                if (params.batch) {
                    const existingRows = await listByBatch(userData.sfid, params.batch);
                    this.setState(
                        await this.buildOwnerState(userData, existingRows, params.batch)
                    );
                    return;
                }

                // New entry (+ FAB) — empty form, new batch key
                this.setState(await this.buildOwnerState(userData, [], makeBatchKey()));
                return;
            }

            // CMDI / AMMD readonly multi for an FCP
            if (
                (userData.role === 'CMDI' || userData.role === 'AMMD' || forcedMode === 'readonly') &&
                focusId
            ) {
                const focused = await getById(focusId);
                if (!focused) {
                    this.setState({ loading: false, mode: 'deny' });
                    return;
                }
                if (userData.role === 'CMDI') {
                    const allowed = await cmdiHasAccessToFcp(userData.sfid, focused.fcp__c);
                    if (!allowed) {
                        this.setState({ loading: false, mode: 'deny' });
                        Swal.fire({
                            icon: 'error',
                            title: 'Access denied',
                            text: 'This FCP is not assigned to you.',
                        });
                        return;
                    }
                }
                if (userData.role === 'AMMD') {
                    const allowed = await ammdHasAccessToFcp(userData.sfid, focused.fcp__c);
                    if (!allowed) {
                        this.setState({ loading: false, mode: 'deny' });
                        Swal.fire({
                            icon: 'error',
                            title: 'Access denied',
                            text: 'This FCP is not under your CMDI hierarchy.',
                        });
                        return;
                    }
                }
                const all = await listByFcp(focused.fcp__c);
                const batchKey = params.batch || getEntryKey(focused);
                const entryRows = (
                    batchKey
                        ? all.filter((r) => getEntryKey(r) === batchKey)
                        : all.filter((r) => String(r.id) === String(focused.id))
                ).sort(
                    (a, b) =>
                        (parseInt(a.submission_number__c, 10) || 0) -
                        (parseInt(b.submission_number__c, 10) || 0)
                );
                const rows = entryRows.length ? entryRows : [focused];
                const submissionDate = focused.submission_date__c
                    ? String(focused.submission_date__c).slice(0, 10)
                    : '';
                this.setState({
                    mode: 'readonly',
                    fcpSfid: focused.fcp__c,
                    fcpDisplayName: focused.fcp_name || '',
                    evaluatedBy: focused.evaluated_by__c
                        ? {
                              label: focused.evaluated_by_name || focused.evaluated_by__c,
                              value: focused.evaluated_by__c,
                          }
                        : null,
                    submissionDate,
                    submissionName: submissionNameFromRows(rows),
                    sections: rows.map(mapRowToSection),
                    remark: remarkFromRows(rows),
                    ...extraRemarksFromRows(rows),
                    batchKey,
                    loading: false,
                });
                return;
            }

            // CMDI/AMMD opening FCP via query fcpSfid
            if (
                (userData.role === 'CMDI' || userData.role === 'AMMD') &&
                params.fcpSfid
            ) {
                if (userData.role === 'CMDI') {
                    const allowed = await cmdiHasAccessToFcp(
                        userData.sfid,
                        params.fcpSfid
                    );
                    if (!allowed) {
                        this.setState({ loading: false, mode: 'deny' });
                        Swal.fire({
                            icon: 'error',
                            title: 'Access denied',
                            text: 'This FCP is not assigned to you.',
                        });
                        return;
                    }
                }
                if (userData.role === 'AMMD') {
                    const allowed = await ammdHasAccessToFcp(
                        userData.sfid,
                        params.fcpSfid
                    );
                    if (!allowed) {
                        this.setState({ loading: false, mode: 'deny' });
                        Swal.fire({
                            icon: 'error',
                            title: 'Access denied',
                            text: 'This FCP is not under your CMDI hierarchy.',
                        });
                        return;
                    }
                }
                const rows = await listByFcp(params.fcpSfid);
                const submissionDate =
                    rows[0] && rows[0].submission_date__c
                        ? String(rows[0].submission_date__c).slice(0, 10)
                        : '';
                this.setState({
                    mode: 'readonly',
                    fcpSfid: params.fcpSfid,
                    fcpDisplayName: params.fcpName || (await this.loadFcpDisplayName(params.fcpSfid)),
                    evaluatedBy: rows[0] && rows[0].evaluated_by__c
                        ? {
                              label: rows[0].evaluated_by_name || rows[0].evaluated_by__c,
                              value: rows[0].evaluated_by__c,
                          }
                        : null,
                    submissionDate,
                    submissionName: submissionNameFromRows(rows),
                    sections: rows.length ? rows.map(mapRowToSection) : [],
                    remark: remarkFromRows(rows),
                    ...extraRemarksFromRows(rows),
                    loading: false,
                });
                return;
            }

            this.setState({ loading: false, mode: 'deny' });
        } catch (err) {
            console.error(err);
            this.setState({ loading: false, mode: 'deny' });
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load ETTC submissions.',
            });
        }
    }

    gotoBack = () => {
        const { backPath, mode, userData } = this.state;
        if (backPath) {
            this.props.history.push(backPath);
            return;
        }
        if (mode === 'evaluator') {
            this.props.history.push('/ettc-evaluations');
            return;
        }
        if (userData && userData.role === 'CMDI') {
            this.props.history.push('/ettc-cmdi-fcps');
            return;
        }
        if (userData && userData.role === 'AMMD') {
            this.props.history.push('/ettc-ammd-cmdis');
            return;
        }
        this.props.history.push('/ettc-submissions');
    };

    promiseEvaluatorOptions = (inputValue) =>
        new Promise(async (resolve) => {
            const { userData } = this.state;
            if (!userData || !userData.sfid) {
                resolve([]);
                return;
            }
            try {
                resolve(await searchFcpEvaluators(inputValue));
            } catch (e) {
                resolve([]);
            }
        });

    updateSection = (key, patch) => {
        this.setState((prev) => ({
            sections: prev.sections.map((s) =>
                s.key === key ? { ...s, ...patch, dirty: true } : s
            ),
        }));
    };

    addMore = () => {
        const { sections, mode } = this.state;
        if (mode !== 'owner') return;
        if (sections.length >= MAX_SUBMISSIONS) {
            Swal.fire({
                icon: 'info',
                title: 'Limit reached',
                text: `Maximum of ${MAX_SUBMISSIONS} submissions per entry allowed.`,
            });
            return;
        }
        const nextNumber =
            Math.max(
                0,
                ...sections.map((s) => parseInt(s.submissionNumber, 10) || 0)
            ) + 1;
        this.setState({
            sections: [...sections, emptySection(Math.min(nextNumber, MAX_SUBMISSIONS))],
        });
    };

    removeNewSection = (key) => {
        const { sections } = this.state;
        const target = sections.find((s) => s.key === key);
        if (!target || target.id) return;
        if (sections.length <= 1) {
            Swal.fire({
                icon: 'info',
                title: 'Required',
                text: 'At least one submission section is required.',
            });
            return;
        }
        this.setState({ sections: sections.filter((s) => s.key !== key) });
    };

    showSizeError = () => {
        Swal.fire({
            icon: 'info',
            title: 'File too large',
            text: `File must be less than ${MAX_UPLOAD_MB} MB.`,
        });
    };

    handleImageChange = (key) => async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        const typeOk =
            ALLOWED_IMAGE_TYPES.includes(file.type) || /\.(jpe?g|png)$/i.test(file.name);
        if (!typeOk) {
            Swal.fire({
                icon: 'info',
                title: 'Invalid file',
                text: 'Please upload a JPG, JPEG, or PNG image only.',
            });
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            this.showSizeError();
            return;
        }

        this.setState({ uploading: true });
        try {
            const document = await getPDFBase64(file);
            const data = await imageUpload({
                id: file.name,
                img: document,
                type: file.type || 'image/jpeg',
            });
            if (data && data.url) {
                this.updateSection(key, {
                    imagePreview: document,
                    imageUrl: data.url,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload failed',
                    text: (data && data.message) || 'Could not upload image.',
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Upload failed',
                text: 'Could not upload image.',
            });
        }
        this.setState({ uploading: false });
    };

    handlePdfChange = (key) => async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;

        if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
            Swal.fire({
                icon: 'info',
                title: 'Invalid file',
                text: 'Please upload a PDF file only.',
            });
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            this.showSizeError();
            return;
        }

        this.setState({ uploading: true });
        try {
            const document = await getPDFBase64(file);
            const data = await pdfUpload({
                id: file.name,
                pdf: document,
                type: file.type || 'application/pdf',
            });
            if (data && data.url) {
                this.updateSection(key, { pdfUrl: data.url });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload failed',
                    text: (data && data.message) || 'Could not upload PDF.',
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Upload failed',
                text: 'Could not upload PDF.',
            });
        }
        this.setState({ uploading: false });
    };

    sectionEditable = (section) => {
        const { mode, editing } = this.state;
        if (mode === 'owner') {
            return !!editing && section.status !== STATUS.APPROVED;
        }
        return false;
    };

    startEditing = () => {
        const { sections, evaluatedBy, submissionDate, submissionName } = this.state;
        this.editSnapshot = {
            sections: sections.map((s) => ({ ...s })),
            evaluatedBy,
            submissionDate,
            submissionName,
        };
        this.setState({ editing: true });
    };

    cancelEditing = () => {
        if (!this.editSnapshot) {
            this.setState({ editing: false });
            return;
        }
        this.setState({
            editing: false,
            sections: this.editSnapshot.sections,
            evaluatedBy: this.editSnapshot.evaluatedBy,
            submissionDate: this.editSnapshot.submissionDate,
            submissionName: this.editSnapshot.submissionName,
        });
    };

    evaluationEditable = (section) => {
        const { mode } = this.state;
        return mode === 'evaluator' && section.status !== STATUS.APPROVED && !!section.id;
    };

    handleOwnerSubmit = async () => {
        const {
            userData,
            fcpDisplayName,
            evaluatedBy,
            submissionDate,
            submissionName,
            sections,
            batchKey,
        } = this.state;

        if (!(submissionName || '').trim()) {
            Swal.fire({
                icon: 'info',
                title: 'Submission name required',
                text: 'Please enter a submission name.',
            });
            return;
        }

        if (!evaluatedBy || !evaluatedBy.value) {
            Swal.fire({
                icon: 'info',
                title: 'Evaluator required',
                text: 'Please select an FCP in Evaluated By.',
            });
            return;
        }

        const newWithDocs = sections.filter((s) => !s.id && (s.imageUrl || s.pdfUrl));
        const dirtyPending = sections.filter(
            (s) => s.id && s.dirty && s.status !== STATUS.APPROVED
        );
        const hasPendingSaved = sections.some(
            (s) => s.id && s.status !== STATUS.APPROVED
        );

        if (!newWithDocs.length && !dirtyPending.length && !hasPendingSaved) {
            Swal.fire({
                icon: 'info',
                title: 'Nothing to submit',
                text: 'Add at least one image or PDF to a new submission, or update a pending one.',
            });
            return;
        }

        for (const s of newWithDocs.concat(dirtyPending)) {
            if (!s.imageUrl && !s.pdfUrl) {
                Swal.fire({
                    icon: 'info',
                    title: 'Document required',
                    text: `Submission ${s.submissionNumber} needs an image or PDF.`,
                });
                return;
            }
        }

        this.setState({ saving: true });
        try {
            const key = batchKey || makeBatchKey();
            await saveOwnerBatch({
                fcpSfid: userData.sfid,
                fcpName: fcpDisplayName || 'FCP',
                evaluatedBySfid: evaluatedBy.value,
                submissionDate,
                sections,
                batchKey: key,
                submissionName: submissionName.trim(),
            });
            await Swal.fire({
                icon: 'success',
                title: 'Submitted',
                text: 'ETTC submissions saved successfully.',
                confirmButtonColor: '#1a5c9a',
            });
            this.props.history.push('/ettc-submissions');
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Submit failed',
                text: (err && err.message) || 'Could not save submissions.',
            });
            this.setState({ saving: false });
        }
    };

    handleEvaluatorSubmit = async () => {
        const {
            userData,
            sections,
            remark,
            overallClayModellingTechniqueOutcome,
            overallFeedbackAdditionalRemarks,
            rightUsageOfProductsMaterials,
            submissionPresentation,
            overallPaintingTechniqueOutcome,
        } = this.state;
        const pending = sections.filter(
            (s) => s.id && s.status !== STATUS.APPROVED && this.evaluationEditable(s)
        );
        if (!pending.length) {
            Swal.fire({
                icon: 'info',
                title: 'Nothing to submit',
                text: 'No pending submissions to evaluate.',
            });
            return;
        }

        for (const s of pending) {
            if (s.score === '' || s.score === null || s.score === undefined) {
                Swal.fire({
                    icon: 'info',
                    title: 'Score required',
                    text: `Please enter Score for Submission ${s.submissionNumber}.`,
                });
                return;
            }
            const scoreNum = Number(s.score);
            if (!Number.isInteger(scoreNum) || scoreNum < 1 || scoreNum > 10) {
                Swal.fire({
                    icon: 'info',
                    title: 'Invalid score',
                    text: `Score for Submission ${s.submissionNumber} must be between 1 and 10.`,
                });
                return;
            }
        }

        const confirm = await Swal.fire({
            icon: 'question',
            title: 'Are you sure?',
            showCancelButton: true,
            confirmButtonText: 'Submit',
            confirmButtonColor: '#1a5c9a',
        });
        if (!confirm.value) return;

        this.setState({ saving: true });
        try {
            for (const s of pending) {
                await submitEvaluation({
                    id: s.id,
                    evaluatorSfid: userData.sfid,
                    score: s.score,
                    evaluationComments: remark,
                    overallClayModellingTechniqueOutcome,
                    overallFeedbackAdditionalRemarks,
                    rightUsageOfProductsMaterials,
                    submissionPresentation,
                    overallPaintingTechniqueOutcome,
                });
            }
            await Swal.fire({
                icon: 'success',
                title: 'Evaluation submitted',
                confirmButtonColor: '#1a5c9a',
            });
            this.props.history.push('/ettc-evaluations');
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Submit failed',
                text: (err && err.message) || 'Could not submit evaluation.',
            });
            this.setState({ saving: false });
        }
    };

    statusClass = (status, isNew) => {
        if (isNew) return 'ettc-status ettc-status--new';
        if (status === STATUS.APPROVED) return 'ettc-status ettc-status--approved';
        return 'ettc-status ettc-status--pending';
    };

    renderSectionDocs = (section, editable) => {
        const { mode } = this.state;
        const showScore = mode === 'evaluator' || mode === 'readonly' || !!section.id;
        const { imagePreview, imageUrl, pdfUrl, key } = section;
        return (
            <div className={`ettc-claim-grid${showScore ? '' : ' ettc-claim-grid--docs-only'}`}>
                <div className="ettc-field">
                    <label className="ettc-label">
                        Image <span className="req">*</span>
                    </label>
                    <div className="ettc-file-row">
                        {editable ? (
                            <>
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                                    ref={(ref) => {
                                        this.fileInputs[`img-${key}`] = ref;
                                    }}
                                    style={{ display: 'none' }}
                                    onChange={this.handleImageChange(key)}
                                />
                                <Button
                                    className="ettc-file-btn"
                                    onClick={() =>
                                        this.fileInputs[`img-${key}`] &&
                                        this.fileInputs[`img-${key}`].click()
                                    }
                                >
                                    {imageUrl ? 'Replace image' : 'Upload image'}
                                </Button>
                            </>
                        ) : null}
                        <span className="ettc-hint">JPG/PNG · max {MAX_UPLOAD_MB} MB</span>
                    </div>
                    {imagePreview || imageUrl ? (
                        <div className="ettc-file-preview">
                            <img
                                src={imagePreview || imageUrl}
                                alt={`Submission ${section.submissionNumber}`}
                            />
                            {imageUrl ? (
                                <a
                                    className="ettc-link"
                                    href={imageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open
                                </a>
                            ) : null}
                        </div>
                    ) : (
                        <span className="ettc-hint">No image uploaded</span>
                    )}
                </div>

                <div className="ettc-field">
                    <label className="ettc-label">
                        PDF <span className="req">*</span>
                    </label>
                    <div className="ettc-file-row">
                        {editable ? (
                            <>
                                <input
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    ref={(ref) => {
                                        this.fileInputs[`pdf-${key}`] = ref;
                                    }}
                                    style={{ display: 'none' }}
                                    onChange={this.handlePdfChange(key)}
                                />
                                <Button
                                    className="ettc-file-btn"
                                    onClick={() =>
                                        this.fileInputs[`pdf-${key}`] &&
                                        this.fileInputs[`pdf-${key}`].click()
                                    }
                                >
                                    {pdfUrl ? 'Replace PDF' : 'Upload PDF'}
                                </Button>
                            </>
                        ) : null}
                        <span className="ettc-hint">PDF · max {MAX_UPLOAD_MB} MB</span>
                    </div>
                    {pdfUrl ? (
                        <div className="ettc-file-preview">
                            <img src={pdfLogo} alt="PDF" style={{ width: 36, height: 36 }} />
                            <a
                                className="ettc-link"
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download / open
                            </a>
                        </div>
                    ) : (
                        <span className="ettc-hint">No PDF uploaded</span>
                    )}
                </div>

                {showScore ? (
                    <div className="ettc-field">
                        <label className="ettc-label">Score</label>
                        <div className="ettc-score-row">
                            <TextField
                                type="number"
                                className="ettc-score-input"
                                disabled={!this.evaluationEditable(section)}
                                value={section.score}
                                placeholder={
                                    this.evaluationEditable(section) ? '1-10' : '—'
                                }
                                inputProps={{ min: 1, max: 10, step: 1 }}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') {
                                        this.updateSection(section.key, { score: '' });
                                        return;
                                    }
                                    const n = parseInt(raw, 10);
                                    if (Number.isNaN(n)) return;
                                    const clamped = Math.min(10, Math.max(1, n));
                                    this.updateSection(section.key, {
                                        score: String(clamped),
                                    });
                                }}
                                margin="none"
                                InputProps={{ disableUnderline: true }}
                            />
                            <span className="ettc-score-max">/ 10</span>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    };

    render() {
        const {
            loading,
            saving,
            uploading,
            mode,
            fcpDisplayName,
            evaluatedBy,
            submissionDate,
            submissionName,
            sections,
            remark,
            editing,
        } = this.state;

        const ownerMode = mode === 'owner';
        const evaluatorMode = mode === 'evaluator';
        const entryApproved =
            sections.length > 0 &&
            sections.every((s) => s.id && s.status === STATUS.APPROVED);
        const hasPendingSaved =
            ownerMode &&
            sections.some((s) => s.id && s.status !== STATUS.APPROVED);
        const ownerEditing = ownerMode && !!editing && !entryApproved;
        const formLocked = mode === 'readonly' || entryApproved;
        const remarkEditable =
            evaluatorMode &&
            !formLocked &&
            sections.some((s) => s.id && s.status !== STATUS.APPROVED);
        const showRemark =
            remarkEditable || !!(remark && String(remark).trim());
        const canAddMore = ownerEditing && sections.length < MAX_SUBMISSIONS;
        const headerEvaluatorEditable = ownerEditing;
        const dateEditable = ownerEditing;
        const totalSubmissions = sections.length;
        const totalScore = sections.reduce((sum, s) => {
            const n = Number(s.score);
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
        const avgScore =
            totalSubmissions > 0
                ? Math.round((totalScore / totalSubmissions) * 10) / 10
                : 0;
        const titlePrefix =
            mode === 'evaluator'
                ? 'Evaluate ETTC'
                : mode === 'readonly'
                ? 'View ETTC'
                : 'ETTC Submission';

        return (
            <div className="ettc-page">
                <div className="ettc-panel">
                    <AppBar position="static">
                        <Toolbar>
                            <IconButton color="inherit" aria-label="Back" onClick={this.gotoBack}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="title" color="inherit">
                                {titlePrefix}
                            </Typography>
                        </Toolbar>
                    </AppBar>

                    {loading || uploading || saving ? (
                        <div className="ettc-loader">
                            <Loader type="Audio" color="#00BFFF" height="50" width="50" />
                        </div>
                    ) : mode === 'deny' ? (
                        <div className="ettc-empty">You do not have access to this page.</div>
                    ) : (
                        <>
                            <div className="ettc-panel__body">
                                <div className="ettc-info-box">
                                    <div className="ettc-info-grid">
                                        <div className="ettc-field ettc-field--name">
                                            <label className="ettc-label">
                                                Submission name <span className="req">*</span>
                                            </label>
                                            <TextField
                                                fullWidth
                                                disabled={!ownerEditing}
                                                value={submissionName || ''}
                                                placeholder="Enter submission name"
                                                onChange={(e) =>
                                                    this.setState({
                                                        submissionName: e.target.value,
                                                    })
                                                }
                                                margin="none"
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </div>
                                        <div className="ettc-field">
                                            <label className="ettc-label">FCP</label>
                                            <TextField
                                                fullWidth
                                                disabled
                                                value={fcpDisplayName || ''}
                                                margin="none"
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </div>
                                        <div className="ettc-field">
                                            <label className="ettc-label">
                                                Submission date <span className="req">*</span>
                                            </label>
                                            <TextField
                                                type="date"
                                                fullWidth
                                                disabled={!dateEditable}
                                                value={submissionDate || ''}
                                                onChange={(e) =>
                                                    this.setState({
                                                        submissionDate: e.target.value,
                                                    })
                                                }
                                                InputLabelProps={{ shrink: true }}
                                                margin="none"
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </div>
                                        <div className="ettc-field">
                                            <label className="ettc-label">
                                                Evaluated by <span className="req">*</span>
                                            </label>
                                            {headerEvaluatorEditable ? (
                                                <div className="ettc-select">
                                                    <Async
                                                        cacheOptions
                                                        defaultOptions
                                                        loadOptions={this.promiseEvaluatorOptions}
                                                        value={evaluatedBy}
                                                        onChange={(value) =>
                                                            this.setState({ evaluatedBy: value })
                                                        }
                                                        placeholder="Select FCP..."
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        styles={{
                                                            menuPortal: (base) => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <TextField
                                                    fullWidth
                                                    disabled
                                                    value={
                                                        (evaluatedBy && evaluatedBy.label) ||
                                                        (sections[0] &&
                                                            sections[0].evaluatedByName) ||
                                                        ''
                                                    }
                                                    margin="none"
                                                    InputProps={{ disableUnderline: true }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {sections.map((section, index) => {
                                    const docsEditable = this.sectionEditable(section);
                                    const isNew = !section.id;
                                    const canRemove =
                                        ownerEditing &&
                                        isNew &&
                                        sections.length > 1;
                                    return (
                                        <div
                                            key={section.key}
                                            id={`submission-${section.id || section.key}`}
                                            className="ettc-claim"
                                        >
                                            <div className="ettc-claim__head">
                                                <h2 className="ettc-claim__title">
                                                    Submission -{' '}
                                                    {section.submissionNumber || index + 1}
                                                    <span
                                                        className={this.statusClass(
                                                            section.status,
                                                            isNew
                                                        )}
                                                    >
                                                        {isNew
                                                            ? 'New'
                                                            : section.status || 'Pending'}
                                                    </span>
                                                </h2>
                                                {ownerEditing && isNew ? (
                                                    <IconButton
                                                        className="ettc-claim__trash"
                                                        aria-label="Remove submission"
                                                        disabled={!canRemove}
                                                        onClick={() =>
                                                            this.removeNewSection(section.key)
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                ) : null}
                                            </div>

                                            {this.renderSectionDocs(section, docsEditable)}
                                        </div>
                                    );
                                })}

                                {canAddMore ? (
                                    <div className="ettc-add-below">
                                        <Button
                                            className="ettc-btn-teal"
                                            onClick={this.addMore}
                                        >
                                            Add New Submission
                                        </Button>
                                    </div>
                                ) : null}

                                {!canAddMore && ownerMode ? (
                                    <p className="ettc-hint" style={{ marginBottom: 16 }}>
                                        {sections.length}/{MAX_SUBMISSIONS} submissions in this
                                        entry (limit reached)
                                    </p>
                                ) : null}

                                {showRemark ? (
                                    <div className="ettc-claim">
                                        <div className="ettc-field">
                                            <label className="ettc-label">Remark</label>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                disabled={!remarkEditable}
                                                value={remark}
                                                placeholder={
                                                    remarkEditable
                                                        ? 'Add evaluation remarks…'
                                                        : ''
                                                }
                                                onChange={(e) =>
                                                    this.setState({ remark: e.target.value })
                                                }
                                                margin="none"
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </div>
                                    </div>
                                ) : null}

                                {EXTRA_REMARK_FIELDS.map((field) => {
                                    const value = this.state[field.key] || '';
                                    if (!remarkEditable && !String(value).trim()) {
                                        return null;
                                    }
                                    return (
                                        <div className="ettc-claim" key={field.key}>
                                            <div className="ettc-field">
                                                <label className="ettc-label">{field.label}</label>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    disabled={!remarkEditable}
                                                    value={value}
                                                    placeholder={
                                                        remarkEditable ? 'Add remarks…' : ''
                                                    }
                                                    onChange={(e) =>
                                                        this.setState({
                                                            [field.key]: e.target.value,
                                                        })
                                                    }
                                                    margin="none"
                                                    InputProps={{ disableUnderline: true }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="ettc-summary-box">
                                    <div className="ettc-summary-item">
                                        <span className="ettc-summary-label">Total submissions</span>
                                        <span className="ettc-summary-value">
                                            {totalSubmissions}
                                        </span>
                                    </div>
                                    <div className="ettc-summary-item">
                                        <span className="ettc-summary-label">Avg score</span>
                                        <span className="ettc-summary-value">
                                            {avgScore}
                                            <span className="ettc-summary-meta">
                                                {' '}
                                                ({totalScore} / {totalSubmissions || 0})
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="ettc-panel__footer">
                                <span className="ettc-hint">
                                    Total {totalSubmissions} · Avg score {avgScore}
                                </span>

                                <div className="ettc-footer-right">
                                    {hasPendingSaved && !editing ? (
                                        <Button
                                            className="ettc-btn-teal"
                                            onClick={this.startEditing}
                                        >
                                            Edit
                                        </Button>
                                    ) : null}
                                    {ownerEditing && hasPendingSaved ? (
                                        <Button className="ettc-btn-ghost" onClick={this.cancelEditing}>
                                            Cancel
                                        </Button>
                                    ) : null}
                                    {ownerEditing ? (
                                        <Button
                                            className="ettc-btn-teal"
                                            onClick={this.handleOwnerSubmit}
                                        >
                                            Submit
                                        </Button>
                                    ) : null}
                                    {evaluatorMode && !formLocked ? (
                                        <Button
                                            className="ettc-btn-teal"
                                            onClick={this.handleEvaluatorSubmit}
                                            disabled={
                                                !sections.some(
                                                    (s) =>
                                                        s.id &&
                                                        s.status !== STATUS.APPROVED
                                                )
                                            }
                                        >
                                            Submit
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default ETTCSubmissionForm;
